import { Component } from "react";
import {
  hasPendingChunkReload,
  isChunkError,
  markChunkReloadRequested,
  resetChunkReloadFlag,
} from "../utils/chunkReload.js";
import Button from "./ui/Button.jsx";

/**
 * Recovery for the PWA's most common failure: a deploy hashes every lazy route
 * bundle, so a phone that kept the old shell open against an offline-first
 * cache can ask for a chunk that no longer exists ("Failed to fetch dynamically
 * imported module"). Reloading once gets the fresh shell + chunk set; anything
 * else needs a human decision, so it gets the manual fallback below.
 *
 * The reload is guarded by a sessionStorage flag so a still-stale cache can
 * never turn into an infinite reload loop — a second failure in the same tab
 * always lands on the recovery card instead.
 */
export default class ChunkErrorBoundary extends Component {
  state = { failed: false, chunkError: false, reloading: false, error: null };

  static getDerivedStateFromError(error) {
    return { failed: true, chunkError: isChunkError(error), error };
  }

  componentDidMount() {
    this.recoverFromChunkError();
  }

  componentDidUpdate() {
    this.recoverFromChunkError();
  }

  /** One automatic reload per deploy, only in production, only for chunk faults. */
  recoverFromChunkError() {
    const { failed, chunkError, reloading } = this.state;
    if (!failed || !chunkError || reloading) return;
    if (!import.meta.env.PROD) return;
    if (hasPendingChunkReload()) return;

    this.setState({ reloading: true });
    markChunkReloadRequested();
    window.location.reload();
  }

  /** Explicit fresh attempt — clears the guard so the reload can fire once again. */
  handleRetry = () => {
    resetChunkReloadFlag();
    this.setState({ failed: false, chunkError: false, reloading: false, error: null });
  };

  handleFullReload = () => {
    resetChunkReloadFlag();
    window.location.reload();
  };

  render() {
    if (!this.state.failed) return this.props.children;

    const { chunkError, reloading, error } = this.state;
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-6 py-16">
        <div className="card-surface w-full max-w-md p-8 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-surface-container text-primary">
            <span className="material-symbols-outlined text-3xl" aria-hidden="true">
              {chunkError ? "cloud_off" : "error"}
            </span>
          </div>
          <h1 className="mt-5 font-headline text-2xl font-extrabold tracking-tight text-on-surface">
            {chunkError ? "App just updated" : "This screen couldn't load"}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">
            {chunkError
              ? "A newer version was just deployed. Reload to pick up the latest update."
              : "Something went wrong while loading this screen. Please try again."}
          </p>

          {import.meta.env.DEV && error?.message && (
            <pre className="mt-4 overflow-x-auto rounded-xl bg-surface-container p-3 text-left font-mono text-xs text-on-surface-variant">
              {String(error.message)}
            </pre>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button variant="primary" onClick={this.handleFullReload} loading={reloading}>
              Reload page
            </Button>
            <Button variant="ghost" onClick={this.handleRetry} disabled={reloading}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }
}